// ImportContactsModal — "Import CSV" from the personal CRM dashboard.
// Real end-to-end flow against /crm/import/contacts: pick a project, pick a
// file, map its columns to contact fields, then show the real import result.
import { useState } from 'react'
import { X, Upload, FileText } from 'lucide-react'
import { importContactsCsv as importContactsCsvApi } from '../../services/api'
import { disambiguateProjectLabels } from '../../utils/projects'
import type { CsvImportResult, Project } from '../../types'

interface ImportContactsModalProps {
  projects: Project[]
  onImported: () => void
  onClose: () => void
  /** Injectable for tests (defaults to the real API call) — avoids mocking
   * the services/api module entirely; tests pass a vi.fn() here instead. */
  importContactsCsv?: typeof importContactsCsvApi
}

const CANONICAL_FIELDS: { value: string; label: string }[] = [
  { value: '', label: '— Skip —' },
  { value: 'first_name', label: 'First name' },
  { value: 'last_name', label: 'Last name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'title', label: 'Title' },
  { value: 'role', label: 'Role' },
  { value: 'organisation_name', label: 'Organisation' },
  { value: 'notes', label: 'Notes' },
]

// Common header spellings mapped to the canonical field the backend expects
// (crm/import_export.py `_CONTACT_FIELDS`). Used only to pre-fill a sensible
// default mapping — the user can still change any column before importing.
const HEADER_ALIASES: Record<string, string> = {
  firstname: 'first_name',
  first: 'first_name',
  givenname: 'first_name',
  lastname: 'last_name',
  surname: 'last_name',
  familyname: 'last_name',
  email: 'email',
  emailaddress: 'email',
  phone: 'phone',
  phonenumber: 'phone',
  mobile: 'phone',
  title: 'title',
  jobtitle: 'title',
  role: 'role',
  organisation: 'organisation_name',
  organisationname: 'organisation_name',
  organization: 'organisation_name',
  organizationname: 'organisation_name',
  company: 'organisation_name',
  org: 'organisation_name',
  notes: 'notes',
  note: 'notes',
}

function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z]/g, '')
}

/** Minimal client-side parse of just the header row, for the mapping UI.
 * The backend does the real row-by-row CSV parsing (csv.DictReader) once the
 * file is sent — this only needs the column names to build the mapping. */
function parseHeaderRow(text: string): string[] {
  const firstLine = text.split(/\r?\n/)[0] ?? ''
  return firstLine
    .split(',')
    .map((h) => h.trim().replace(/^"|"$/g, ''))
    .filter(Boolean)
}

export default function ImportContactsModal({
  projects,
  onImported,
  onClose,
  importContactsCsv = importContactsCsvApi,
}: ImportContactsModalProps) {
  const labelledProjects = disambiguateProjectLabels(projects)
  const [projectId, setProjectId] = useState(labelledProjects[0]?.project_id ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<CsvImportResult | null>(null)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null
    setError('')
    setResult(null)
    setFile(f)
    if (!f) {
      setHeaders([])
      setMapping({})
      return
    }
    try {
      const text = await f.text()
      const cols = parseHeaderRow(text)
      setHeaders(cols)
      const auto: Record<string, string> = {}
      for (const col of cols) {
        const alias = HEADER_ALIASES[normalizeHeader(col)]
        if (alias) auto[col] = alias
      }
      setMapping(auto)
    } catch {
      setError('Could not read that file. Please choose a plain CSV file.')
      setHeaders([])
      setMapping({})
    }
  }

  const handleImport = async () => {
    if (!file || !projectId) return
    setImporting(true)
    setError('')
    try {
      // Only send columns the user actually mapped to a field.
      const columnMapping = Object.fromEntries(Object.entries(mapping).filter(([, v]) => !!v))
      const res = await importContactsCsv(projectId, file, columnMapping)
      setResult(res)
      if (res.imported > 0) onImported()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  const hasMappedFirstName = Object.values(mapping).includes('first_name')

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
          <h3 className="text-base font-semibold text-gray-900">Import Contacts from CSV</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600"><X size={18} /></button>
        </div>

        {labelledProjects.length === 0 ? (
          <div className="p-6">
            <p className="text-sm text-gray-600">You need a project before you can import contacts.</p>
          </div>
        ) : result ? (
          <div className="p-6 space-y-4">
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 space-y-1">
              <p className="text-sm font-medium text-gray-900">{result.imported} contact{result.imported === 1 ? '' : 's'} imported</p>
              {result.skipped > 0 && (
                <p className="text-xs text-gray-600">
                  {result.skipped} row{result.skipped === 1 ? '' : 's'} skipped{result.duplicates > 0 ? ` (${result.duplicates} duplicate email${result.duplicates === 1 ? '' : 's'})` : ''}
                </p>
              )}
            </div>
            {result.errors?.length > 0 && (
              <div className="text-xs text-gray-600 max-h-40 overflow-y-auto border rounded-lg divide-y">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-3 py-1.5">
                    Row {e.row}: {(e as { message?: string; reason?: string }).message ?? (e as { message?: string; reason?: string }).reason ?? 'Could not import this row'}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600">Done</button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>}

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Import into project *</label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
              >
                {labelledProjects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">CSV file *</label>
              {!file ? (
                <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl p-6 cursor-pointer hover:border-sky-300 hover:bg-gray-50 transition-colors">
                  <Upload size={20} className="text-gray-500" />
                  <span className="text-sm text-sky-600 font-medium">Choose a CSV file</span>
                  <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" />
                </label>
              ) : (
                <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
                  <FileText size={14} className="text-gray-500 shrink-0" />
                  <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => { setFile(null); setHeaders([]); setMapping({}); setError(''); setResult(null) }}
                    className="text-xs text-gray-500 hover:text-red-500 shrink-0"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {headers.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Map columns</label>
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {headers.map((h) => (
                    <div key={h} className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 flex-1 truncate" title={h}>{h}</span>
                      <select
                        value={mapping[h] ?? ''}
                        onChange={(e) => setMapping((prev) => ({ ...prev, [h]: e.target.value }))}
                        className="px-2 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white w-40 shrink-0"
                      >
                        {CANONICAL_FIELDS.map((f) => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                {!hasMappedFirstName && (
                  <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Map a column to "First name" — rows without one will be skipped.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || !file || !projectId}
                className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50"
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
